// Normalização de telefone para importação de leads (frontend).
// Regras BR: aceita "+55...", "0055...", "(51) 98559-9432", "5551855994 32", etc.
// Sempre devolve dígitos com DDI e, para celulares BR, com o 9º dígito.

const BR_DDDS = new Set<number>([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

function onlyDigits(raw: string): string {
  let cleaned = String(raw ?? "").trim();

  // Notação científica vinda de planilhas (ex.: "5.5519E+12")
  if (/^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(cleaned)) {
    const num = Number(cleaned);
    if (Number.isFinite(num)) cleaned = BigInt(Math.round(num)).toString();
  }

  cleaned = cleaned.replace(/\D/g, "");

  // Prefixo internacional discado "00"
  if (cleaned.length > 12 && cleaned.startsWith("00")) cleaned = cleaned.slice(2);

  // "55" + "0" de tronco/operadora: 55 053 9… -> 55 53 9…
  if (cleaned.startsWith("550") && (cleaned.length === 13 || cleaned.length === 14)) {
    const ddd = parseInt(cleaned.slice(3, 5), 10);
    if (BR_DDDS.has(ddd)) cleaned = `55${cleaned.slice(3)}`;
  }
  // Discagem nacional "0" + DDD (ou "0XX" operadora) sem DDI: 053 9…, 0 21 53 9…
  if (cleaned.startsWith("0") && !cleaned.startsWith("00") && (cleaned.length === 11 || cleaned.length === 12)) {
    const ddd = parseInt(cleaned.slice(1, 3), 10);
    if (BR_DDDS.has(ddd)) cleaned = cleaned.slice(1);
  }
  if (/^0[1-9]{2}[1-9]{2}\d{8,9}$/.test(cleaned) && (cleaned.length === 13 || cleaned.length === 14)) {
    const ddd = parseInt(cleaned.slice(3, 5), 10);
    if (BR_DDDS.has(ddd)) cleaned = cleaned.slice(3);
  }

  return cleaned;
}

/** Remove "0"/"0XX" de discagem nacional e DDI 55 duplicado digitados no campo local. */
export function stripBrDialPrefixes(localDigits: string): string {
  let d = String(localDigits ?? "").replace(/\D/g, "");
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) d = d.slice(2);
  return onlyDigits(d);
}

/**
 * Normaliza para o formato canônico salvo no banco.
 * Ex.: "+555185599432" -> "5551985599432"
 */
export function normalizeImportPhone(phone?: string | null): string | null {
  if (phone === null || phone === undefined) return null;
  const digits = onlyDigits(String(phone));
  if (!digits) return null;

  // Já com DDI Brasil
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    const ddd = parseInt(digits.slice(2, 4), 10);
    const local = digits.slice(4);
    if (BR_DDDS.has(ddd)) {
      // Celular antigo (8 dígitos iniciando em 6-9) -> insere o 9º dígito
      if (local.length === 8 && /^[6-9]/.test(local)) return `55${digits.slice(2, 4)}9${local}`;
      return digits;
    }
  }

  // Sem DDI: 11 dígitos (celular) ou 10 (fixo/celular antigo)
  if (digits.length === 11) {
    const ddd = parseInt(digits.slice(0, 2), 10);
    if (BR_DDDS.has(ddd)) return `55${digits}`;
  }
  if (digits.length === 10) {
    const ddd = parseInt(digits.slice(0, 2), 10);
    const local = digits.slice(2);
    if (BR_DDDS.has(ddd)) {
      if (/^[6-9]/.test(local)) return `55${digits.slice(0, 2)}9${local}`;
      return `55${digits}`;
    }
  }

  // Internacional ou formato desconhecido: devolve os dígitos
  return digits;
}

/**
 * Variantes usadas para casar com registros existentes que podem estar
 * salvos com ou sem o 9º dígito.
 */
export function buildImportPhoneVariants(phone?: string | null): string[] {
  const normalized = normalizeImportPhone(phone);
  if (!normalized) return [];
  const variants = new Set<string>([normalized]);

  if (normalized.startsWith("55")) {
    const ddd = normalized.slice(2, 4);
    const local = normalized.slice(4);
    if (local.length === 9 && local.startsWith("9")) variants.add(`55${ddd}${local.slice(1)}`);
    if (local.length === 8) variants.add(`55${ddd}9${local}`);
    variants.add(normalized.slice(2)); // sem DDI (dados legados)
  }

  return Array.from(variants);
}
